import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { NativeSelect, NativeSelectOption } from "../components/ui/native-select"
import { cn } from "../lib/utils"
import { liftV4Props } from "./element-renderer"

export function NativeSelectRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()

  const rawOptions = (element as unknown as Record<string, unknown>).options
  const resolvedOptions = typeof rawOptions === "string" ? resolveSymbol(rawOptions) : rawOptions
  const options = Array.isArray(resolvedOptions)
    ? (resolvedOptions as Array<{ label?: string; value?: string }>)
    : []
  // JITRULE Task 2: authored `<option>` children may nest their fields
  // under `props`. `options` (plain `{label,value}` config data, not v4
  // elements) is intentionally left as-is — see property-value-utils
  // migration report.
  const authoredOptions = (element.children ?? []).map(liftV4Props)

  const defaultValue = element.defaultValue
    ? String(resolveSymbol(String(element.defaultValue)) ?? "")
    : undefined
  const value = element.value !== undefined
    ? String(resolveSymbol(String(element.value)) ?? element.value)
    : undefined
  const authoredSize = element.size
  const nativeSize = authoredSize === "sm" ? "sm" : "default"
  const sizeClass = authoredSize === "lg" ? "[&_select]:h-12 [&_select]:text-base" : undefined

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value: e.target.value })
  }

  return (
    <NativeSelect
      className={cn(sizeClass, element.className)}
      size={nativeSize}
      {...(value !== undefined ? { value } : { defaultValue })}
      onChange={handleChange}
      aria-label={element.ariaLabel ?? element.label ?? "Select"}
    >
      {authoredOptions.map((option, i) => {
        const optionValue = String(
          resolveSymbol(String(option.value ?? option.variant ?? option.id))
            ?? option.value
            ?? option.variant
            ?? option.id,
        )
        const optionLabel = String(
          resolveSymbol(String(option.text ?? option.label ?? option.value ?? option.id))
            ?? option.text
            ?? option.label
            ?? option.value
            ?? option.id,
        )
        return (
          <NativeSelectOption
            key={option.id || i}
            value={optionValue}
            className={option.className}
            disabled={option.disabled === true || option.disabled === "true"}
            data-cogo-element-id={option.id}
            data-cogo-type={option.type}
          >
            {optionLabel}
          </NativeSelectOption>
        )
      })}
      {options.map((opt, i) => (
        <NativeSelectOption key={opt.value ?? i} value={opt.value ?? ""}>
          {String(resolveSymbol(opt.label ?? "") ?? "")}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  )
}
